import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { match } from 'ts-pattern'

import { ExitPrompt, ExitPromptType } from '@frame/ui-components/exit-prompt'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { H2 } from '@frame/ui-primitives/h2'
import { P } from '@frame/ui-primitives/p'

import { TaskFooter } from '@frame/components/screens/tasks/components/task-footer'
import { useVerifyTask } from '@frame/components/screens/tasks/hooks/useVerifyTask'
import { FeatureFlagList, VerifierType } from '@frame/lib/api/common/types'
import { InternalEventType } from '@frame/lib/api/events/types'
import { buildEmployerHistoryViewProps } from '@frame/lib/api/events/utils'
import { DirectTask, DirectTaskState, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { verifierRequirementsMapping } from '@frame/lib/constants'
import { useRootContext } from '@frame/lib/context'
import { create } from '@frame/lib/errors'
import useChosenVerifierRequirements from '@frame/lib/hooks/useChosenVerifierRequirements'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useFeatureEnabled } from '@frame/lib/hooks/useFeatures'
import { useLoginItems } from '@frame/lib/hooks/useLoginItems'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useStore } from '@frame/lib/stores/useStore'
import { getTextForVerificationType } from '@frame/lib/utils/session'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'
import { ErrorCode } from '@script/types'

import { TaskList } from '../components/task-list'
import { ConnectBankCard } from './income-only-tasks/connect-bank-card'
import { EmployerHistoryCard } from './income-only-tasks/employer-history-card'

type Props = {
  tasks: DirectTask[]
  backButton?: {
    visible?: boolean
    onClick?: () => void
  }
}

export function EmploymentIncomeTasks({ tasks, backButton }: Props) {
  const navigate = useNavigate()
  const { actions } = useRootContext()
  const { handleEvent } = useEventHandler()
  const { handleConnectBank, handleVerify } = useVerifyTask()
  const { bankEnabled, bilEnabledStrategies, credentialsEnabled } = useStrategies()

  const {
    maxEmployments,
    consumerInitiated,
    disableOtherIncomeFlow,
    skipApplicantStatedInfo,
    docsForMortgageEnabled,
    reportType,
    verifierType,
    setError,
  } = useStore(({ session, setError }) => ({
    maxEmployments: session?.max_employments ?? 1,
    consumerInitiated: session?.consumer_initiated,
    disableOtherIncomeFlow: session?.configuration.settings?.disable_other_income_flow ?? false,
    skipApplicantStatedInfo: session?.configuration.skip_applicant_stated_info ?? false,
    docsForMortgageEnabled: session?.configuration.docs_for_mortgage_enabled ?? false,
    reportType: session?.verification_type,
    verifierType: session?.verifier_type ?? undefined,
    setError,
  }))
  const chosenVerifierRequirements = useChosenVerifierRequirements()

  const [preparingEmploymentId, setPreparingEmploymentId] = useState<string | null>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [showIncompletePrompt, setShowIncompletePrompt] = useState(false)
  const loginItemIds = useLoginItems()

  const bankAndPosEnabled = useFeatureEnabled(FeatureFlagList.BankPOS)

  const isBankEnabled = verifierType === VerifierType.MORTGAGE && bankEnabled

  // TODO AZALEA: if isBankOnly, do we want to hide this option?
  const canAddEmployer = tasks.length < maxEmployments && !loginItemIds.length && !isBankEnabled

  useEffect(() => {
    if (showWarning) {
      const timeout = setTimeout(() => {
        setShowWarning(false)
      }, 3000)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [showWarning]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleVerifyTask(task: DirectTask, index?: number) {
    setPreparingEmploymentId(task.id)
    const params = new URLSearchParams({ q: task.employer_name ?? '' })

    if (task.state === DirectTaskState.SKIPPED) {
      await actions.unSkipDirectTask(task.id)
    }

    navigateToTaskRoute(task, params, index)
  }

  function navigateToTaskRoute(task: DirectTask, params: URLSearchParams, index?: number) {
    const isMultiStrategy = bilEnabledStrategies.length > 1
    const isTaxStrategy = task.strategy === TrueworkJsStrategy.IRS
    const isBankStrategy = task.strategy === TrueworkJsStrategy.BANK
    const isDocumentsStrategy = task.strategy === TrueworkJsStrategy.DOCUMENTS

    if (isBankStrategy && !isBankEnabled) {
      create({
        error: new Error(`Bank is not supported for report type: ${reportType}`),
        code: ErrorCode.Display,
        displayReason: `Failed to start bank connection for your ${getTextForVerificationType(
          reportType
        )} verification`,
        setError,
      })
      navigate(routes.error, { replace: true })
    } else if (isTaxStrategy) {
      handleEvent(
        InternalEventType.IRS_TASK_SELECTED,
        buildEmployerHistoryViewProps(canAddEmployer, bilEnabledStrategies, tasks, index)
      )
      navigate(`${routes.taxLink}/${task.id}`)
    } else if (consumerInitiated && !skipApplicantStatedInfo) {
      navigate(`${routes.statedInformation}/${task.id}?${params.toString()}`, {
        state: { task },
      })
    } else if (isMultiStrategy) {
      if (docsForMortgageEnabled) {
        if (isDocumentsStrategy) {
          navigate(`${routes.documents}/${task.id}?${params.toString()}`)
        } else {
          navigate(`${routes.companySearch}/${task.id}?${params.toString()}`)
        }
      } else {
        navigate(`${routes.selectVerificationMethod}/${task.id}?${params.toString()}`)
      }
    } else {
      if (isDocumentsStrategy) {
        navigate(`${routes.documents}/${task.id}?${params.toString()}`)
      } else {
        navigate(`${routes.companySearch}/${task.id}?${params.toString()}`)
      }
    }

    handleEvent(
      InternalEventType.STARTED_EMPLOYER_VERIFICATION_FLOW,
      buildEmployerHistoryViewProps(canAddEmployer, bilEnabledStrategies, [task], index)
    )
  }

  function handleAddEmployer() {
    handleEvent(
      InternalEventType.EMPLOYER_HISTORY_ADD_EMPLOYER,
      buildEmployerHistoryViewProps(canAddEmployer, bilEnabledStrategies, tasks)
    )

    if (bilEnabledStrategies.length === 1) {
      addEmployerSingleStrategy()
    } else {
      addEmployerMultiStrategy()
    }
  }

  function addEmployerSingleStrategy() {
    return match(bilEnabledStrategies[0])
      .with(TrueworkJsStrategy.CREDENTIALS, () => {
        if (skipApplicantStatedInfo) {
          navigate(routes.companySearch)
        } else {
          navigate(routes.statedInformation)
        }
      })
      .with(TrueworkJsStrategy.DOCUMENTS, () => {
        if (skipApplicantStatedInfo) {
          navigate(routes.documents)
        } else {
          navigate(routes.statedInformation)
        }
      })
      .with(TrueworkJsStrategy.BANK, () => {
        throw new Error('Bank is not supported for add employer')
      })
      .with(TrueworkJsStrategy.EMPLOYMENT_STRATEGIES, () => {
        throw new Error('Bank is not supported for add employer')
      })
      .with(TrueworkJsStrategy.SMART_OUTREACH, () => {
        throw new Error('Smart outreach is not supported for add employer')
      })
      .with(TrueworkJsStrategy.INSTANT, () => {
        throw new Error('Instant is not supported for add employer')
      })
      .with(TrueworkJsStrategy.IRS, () => {
        throw new Error('Tax is not supported for add employer')
      })
      .exhaustive()
  }

  function addEmployerMultiStrategy() {
    if (disableOtherIncomeFlow && consumerInitiated) {
      navigate(routes.statedInformation)
    } else {
      navigate(routes.selectIncomeType)
    }
  }

  return (
    <>
      <WidgetHeaderWithNavigation showCobranding backButton={backButton} showExitPrompt={tasks?.length > 0} />
      <Body data-cy={DataAttribute.POS_SCREEN_TASKS} className="p-5" paddingTopOverride={8}>
        <H2 className="mb-1 text-left">Complete your tasks</H2>
        <P className="text-left">Provide {verifierRequirementsMapping[chosenVerifierRequirements]}</P>
        {bankAndPosEnabled || !bankEnabled ? (
          <TaskList
            tasks={tasks}
            loadingTaskId={preparingEmploymentId}
            handleVerify={handleVerifyTask}
            handleAddEmployer={canAddEmployer ? handleAddEmployer : undefined}
          />
        ) : (
          <div className="mt-5 flex flex-col space-y-4">
            <ConnectBankCard handleConnectBank={() => handleConnectBank(tasks[0])} />
            {credentialsEnabled && <EmployerHistoryCard task={tasks[0]} handleVerify={handleVerify} />}
          </div>
        )}
      </Body>
      <ExitPrompt
        show={showIncompletePrompt}
        hide={() => setShowIncompletePrompt(false)}
        title="Submit incomplete tasks?"
        type={ExitPromptType.INCOMPLETE}
      />
      <TaskFooter setShowIncompletePrompt={setShowIncompletePrompt} directTasks={tasks} />
    </>
  )
}
